import React, { useState } from 'react';
import { Loader2, Bell, Trophy, Users, Calendar, DollarSign } from 'lucide-react';
import './TournamentNotificationTest.css';

const TournamentNotificationTest = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [testData, setTestData] = useState({
    tournamentTitle: 'Yu-Gi-Oh! Championship Series',
    tournamentFormat: 'Advanced',
    tournamentFees: '$25',
    tournamentDate: new Date().toLocaleDateString()
  });

  const handleInputChange = (field, value) => {
    setTestData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const simulateTournamentCreation = async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const timestamp = Date.now();
      const tournamentId = `test-tournament-${timestamp}`;
      
      const payload = {
        tournamentId: tournamentId,
        tournamentTitle: testData.tournamentTitle,
        tournamentFormat: testData.tournamentFormat,
        tournamentFees: testData.tournamentFees,
        tournamentDate: testData.tournamentDate
      };

      console.log('🧪 Sending tournament notification test...', payload);

      const response = await fetch('https://testtournamentnotification-3jpa5kiweq-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📊 Test result:', data);
      
      setResult(data);
      
    } catch (err) {
      console.error('❌ Error testing tournament notification:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetTest = () => {
    setResult(null);
    setError(null);
    setTestData({
      tournamentTitle: 'Yu-Gi-Oh! Championship Series',
      tournamentFormat: 'Advanced',
      tournamentFees: '$25',
      tournamentDate: new Date().toLocaleDateString()
    });
  };

  return (
    <div className="tournament-test-container">
      <div className="tournament-test-content">
        {/* Header */}
        <div className="tournament-test-header">
          <div className="header-icon">
            <Bell className="icon" />
            <h1>Tournament Notification Test</h1>
          </div>
          <p className="header-description">
            Test the tournament notification system by simulating tournament creation. 
            This will send notifications to all users with valid FCM tokens.
          </p>
        </div>

        <div className="tournament-test-grid">
          {/* Test Configuration */}
          <div className="test-card">
            <div className="card-header">
              <Trophy className="card-icon" />
              <h2>Test Configuration</h2>
            </div>
            <div className="card-content">
              <div className="form-group">
                <label htmlFor="tournamentTitle">Tournament Title</label>
                <input
                  id="tournamentTitle"
                  type="text"
                  value={testData.tournamentTitle}
                  onChange={(e) => handleInputChange('tournamentTitle', e.target.value)}
                  placeholder="Enter tournament title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tournamentFormat">Tournament Format</label>
                <input
                  id="tournamentFormat"
                  type="text"
                  value={testData.tournamentFormat}
                  onChange={(e) => handleInputChange('tournamentFormat', e.target.value)}
                  placeholder="e.g., Advanced, Traditional"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tournamentFees">Tournament Fees</label>
                <input
                  id="tournamentFees"
                  type="text"
                  value={testData.tournamentFees}
                  onChange={(e) => handleInputChange('tournamentFees', e.target.value)}
                  placeholder="e.g., $25, Free"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tournamentDate">Tournament Date</label>
                <input
                  id="tournamentDate"
                  type="text"
                  value={testData.tournamentDate}
                  onChange={(e) => handleInputChange('tournamentDate', e.target.value)}
                  placeholder="Tournament date"
                />
              </div>

              <div className="button-group">
                <button
                  className="test-button"
                  onClick={simulateTournamentCreation}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="button-icon spinning" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Bell className="button-icon" />
                      Test Notifications
                    </>
                  )}
                </button>
                
                <button
                  className="reset-button"
                  onClick={resetTest}
                  disabled={isLoading}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="test-card">
            <div className="card-header">
              <Users className="card-icon" />
              <h2>Test Results</h2>
            </div>
            <div className="card-content">
              {error && (
                <div className="error-alert">
                  <strong>Error:</strong> {error}
                </div>
              )}

              {result && (
                <div className="results-section">
                  <div className={`status-alert ${result.success ? 'success' : 'error'}`}>
                    <strong>{result.success ? '✅ Success!' : '❌ Failed'}</strong>
                    <br />
                    {result.message}
                  </div>

                  <div className="results-grid">
                    <div className="result-item">
                      <Calendar className="result-icon" />
                      <div className="result-content">
                        <span className="result-label">Tournament ID</span>
                        <span className="result-value">{result.tournamentId}</span>
                      </div>
                    </div>

                    <div className="result-item">
                      <Users className="result-icon" />
                      <div className="result-content">
                        <span className="result-label">Users Processed</span>
                        <span className="result-value">{result.usersProcessed}</span>
                      </div>
                    </div>

                    <div className="result-item success">
                      <div className="result-icon success-icon"></div>
                      <div className="result-content">
                        <span className="result-label">Success Count</span>
                        <span className="result-value">{result.successCount}</span>
                      </div>
                    </div>

                    <div className="result-item error">
                      <div className="result-icon error-icon"></div>
                      <div className="result-content">
                        <span className="result-label">Error Count</span>
                        <span className="result-value">{result.errorCount}</span>
                      </div>
                    </div>
                  </div>

                  {result.successCount > 0 && (
                    <div className="success-alert">
                      <strong>🎉 Notifications Sent!</strong>
                      <br />
                      {result.successCount} users received the tournament notification successfully.
                    </div>
                  )}

                  {result.errorCount > 0 && (
                    <div className="warning-alert">
                      <strong>⚠️ Some Notifications Failed</strong>
                      <br />
                      {result.errorCount} notifications failed to deliver. This is normal in test environments with invalid FCM tokens.
                    </div>
                  )}
                </div>
              )}

              {!result && !error && (
                <div className="empty-state">
                  <Bell className="empty-icon" />
                  <p>Click "Test Notifications" to simulate tournament creation</p>
                  <p className="empty-subtitle">This will send notifications to all users with valid FCM tokens</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Panel */}
        <div className="info-card">
          <div className="card-header">
            <DollarSign className="card-icon" />
            <h2>How It Works</h2>
          </div>
          <div className="card-content">
            <div className="info-grid">
              <div className="info-step">
                <div className="step-number">1</div>
                <h3>Configure Test Data</h3>
                <p>Set tournament title, format, fees, and date for the test notification.</p>
              </div>
              
              <div className="info-step">
                <div className="step-number">2</div>
                <h3>Send Test Notification</h3>
                <p>Click the button to trigger the Firebase Function that sends notifications to all users.</p>
              </div>
              
              <div className="info-step">
                <div className="step-number">3</div>
                <h3>View Results</h3>
                <p>See how many users received the notification and any errors that occurred.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentNotificationTest;
